import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Hash } from "../../../api/@types/apiTypes";
import { SlotStates } from "../../../api/models/ScheduleSlotModel";
import ScheduleCalendar from "../../../components/calendar/ScheduleCalendar";
import DropDown from "../../../components/forms/dropdown/DropDown";
import useCalendarViewDate from "../../../hooks/useCalendarViewDate";
import { useTelawah } from "../../../hooks/useTelawah";
import CalendarScheduleModel from "../../../models/CalendarScheduleModel";
// TODO: Remove the following mock data
import AppButton from "../../../components/buttons/AppButton";
import { indexRecitationSchedule } from "../../../api/endpoints/candidate/recitationScheduleSlots";
import { makeApiRequest } from "../../../helpers";
import { ApiResponse } from "../../../api/apiResponse";
import CalendarScheduleSlotModel from "../../../models/CalendarScheduleSlotModel";
import ApiDateParser from "../../../helpers/parsers/apiDateParser";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { RequestQualifiedCandidateSchedules } from "../../../api/endpoints/candidate/sessions";
import { ToastContext } from "../../../App";
import CalendarScheduleCandidate from "../../../api/models/CalendarScheduleCandidate";

export default function CandidateScheduleView() {
  const LocalBasePath = "page.candidate.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const authContext = useAuth();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { telawahOptions = [] } = useTelawah();

  const [selectedTelawahId, setSelectedTelawah] = useState<
    number | string | null | undefined
  >(authContext?.currentUser?.telawah_id);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [schedule, setSchedule] = useState<CalendarScheduleModel | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<
    CalendarScheduleSlotModel[]
  >([]);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    setSelectedSlots([]);
    setSchedule(null);
    if (!!selectedTelawahId) {
      fetchRecitationSlots([]);
    }
  }, [selectedTelawahId]);

  useEffect(() => {
    if (!!selectedSlots && selectedSlots.length > 0) {
      fetchRecitationSlots(selectedSlots, generateApiData(selectedSlots));
    }
  }, [selectedSlots]);

  const generateApiData = (slots: CalendarScheduleSlotModel[]) => {
    //   {
    //     "day_time": {
    //         "saturday": [
    //             "2022-11-26T22:00:00.000Z"
    //         ]
    //     }
    //    }
    if (!slots.length) return;
    let data: Hash<any> = {};
    data["day_time"] = {};
    slots.forEach((slot: CalendarScheduleSlotModel) => {
      if (!data["day_time"][slot.dayOfWeekName])
        data["day_time"][slot.dayOfWeekName] = [];
      const time = ApiDateParser.toNormalizedApiDate(slot.startTime);
      data["day_time"][slot.dayOfWeekName].push(time);
    });
    return data;
  };

  const fetchRecitationSlots = async (
    selectedSlots: CalendarScheduleSlotModel[],
    slotData?: object
  ) => {
    setIsLoading(true);
    await makeApiRequest(
      () => indexRecitationSchedule(selectedTelawahId!, slotData),
      (res: ApiResponse) => {
            let schedule = new CalendarScheduleModel(
         res.data,
          SlotStates.available,
          authContext?.currentUser
        );
        selectedSlots.forEach((slot) => {
          schedule.modifySlotsStateByCalendarId(slot.calendarId, SlotStates.booked);
        });
        setSchedule(schedule);
        setIsLoading(false);
      }
    );
  };

  const onSlotClick = (slot: CalendarScheduleSlotModel) => {
    if (slot.state === SlotStates.available) {
      setSelectedSlots([...selectedSlots, slot]);
    } else if (slot.state === SlotStates.booked) {
      setSelectedSlots(selectedSlots.filter((s) => s.calendarId !== slot.calendarId));
    }
  };

  const resetCalendar = () => {
    setSelectedTelawah(authContext?.currentUser?.telawah_id);
    setSelectedSlots([]);
    setSchedule(null);
  };

  const handleSubmit = async () => {
    const newSlots: any = [];
    selectedSlots.forEach((slot) => {
      newSlots.push(new CalendarScheduleCandidate(slot));
    });
    const response: ApiResponse = await RequestQualifiedCandidateSchedules({
      telawah_id:selectedTelawahId!,
      schedule_slots_attributes: newSlots,
    });
    if (response.isSuccess) {
      setIsDone(true);
    } else {
      response.errorMessages.forEach((errorMessage) => {
        toast?.error(errorMessage);
      });
      setIsDone(false);
    }
  };

  const handleCloseModal = () => {
    setIsDone(false);
    navigate("/candidate/dashboard/unscheduled-reader");
  };
  return (
    <div className="flex flex-col w-screen h-screen overflow-y-auto divide-y divide-gray-200">
      <div className="flex flex-col justify-between gap-5 p-6 pb-4">
        <h2 className="text-lg font-semibold leading-8 text-gray-900">
          {t("title")}
        </h2>
        <DropDown
          name={t("chooseTelawah")}
          setValue={(val: any) => setSelectedTelawah(val)}
          defaultValue={`${selectedTelawahId}`}
          label={t("telawah")}
          placeholder={t("chooseTelawah")}
          error={false}
          options={telawahOptions}
          className="w-full"
          disabled
        />
        <p className="text-xs font-medium text-gray-400">{t("disclaimer")}</p>
      </div>
      <ScheduleCalendar
        selectedDate={viewingDate}
        viewingDate={viewingDate}
        schedule={schedule}
        onChangeViewingDate={onChangeViewingDate}
        onSlotClick={onSlotClick}
        compact={true}
        showDates={false}
        isLoading={isLoading}
        highlightSelectedDate={false}
      />
      {!!selectedSlots.length && (
        <div className="flex justify-end flex-shrink-0 gap-4 px-6 py-4">
          <AppButton buttonShape="secondary" onClick={resetCalendar}>
            {t("reSchedule")}
          </AppButton>
          <AppButton buttonShape="primary" onClick={handleSubmit}>
            {t("send")}
          </AppButton>
        </div>
      )}
      <ConfirmationModal
        isOpen={isDone}
        btnText={"goToTheMeeting"}
        onClose={handleCloseModal}
        email={authContext?.currentUser?.email || ""}
      />
    </div>
  );
}
